import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ExternalLink, Trash2, Plus, Bot, ChevronDown } from "lucide-react";
import { ReceiptRobotSection } from "./ReceiptRobotSection";

/**
 * Beleg-Portale: Direktlinks zu Rechnungsseiten + eigene Webseiten + Auto-Abruf-Roboter.
 * focusPortals (aus dem Bankabgleich): nur die erkannten Portale zeigen (z.B. Digitec + Galaxus),
 * alle anderen erst auf Klick «Andere Portale anzeigen».
 */
export const ReceiptSourcesDialog = ({ open, onOpenChange, companyId, initialPreset = null, focusPortals = null }) => {
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState({ name: "", url: "" });
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const focused = !!focusPortals?.length && !showAll;

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const r = await apiClient.get(`/companies/${companyId}/receipt-sources`);
      setSources(r.data.sources || []);
    } catch { /* ignore */ }
  }, [companyId]);

  useEffect(() => { if (open) { load(); setShowAll(false); } }, [open, load]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name || !form.url) return;
    setBusy(true);
    try {
      await apiClient.post(`/companies/${companyId}/receipt-sources`, form);
      toast.success("Portal hinzugefügt");
      setForm({ name: "", url: "" });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await apiClient.delete(`/companies/${companyId}/receipt-sources/${id}`);
      await load();
    } catch { toast.error("Löschen fehlgeschlagen"); }
  };

  const grouped = sources.reduce((acc, s) => {
    (acc[s.category] = acc[s.category] || []).push(s);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="receipt-sources-dialog">
        <DialogHeader>
          <DialogTitle>Beleg-Portale — Quittungen schnell finden</DialogTitle>
        </DialogHeader>

        {focusPortals?.length > 0 && (
          <div className="rounded-md border border-indigo-200 bg-indigo-50/60 p-3" data-testid="receipt-sources-focus">
            <p className="text-xs font-medium text-indigo-900 flex items-center gap-1.5">
              <Bot className="h-3.5 w-3.5" /> Erkannt: {focusPortals.map((p) => p.name).join(" / ")}
            </p>
            {focusPortals.length > 1 && (
              <p className="text-[11px] text-indigo-800/80 mt-0.5">Der Beleg liegt in einem dieser Shops – beide separat prüfen.</p>
            )}
            <div className="mt-2 space-y-1">
              {focusPortals.map((p) => (
                <a key={p.key} href={p.invoices_url || p.login_url} target="_blank" rel="noreferrer"
                  className="flex items-center gap-1.5 text-sm font-medium text-indigo-800 hover:text-indigo-950 bg-white border border-indigo-100 rounded-md px-2.5 py-1.5"
                  data-testid={`receipt-sources-focus-link-${p.key}`}>
                  <ExternalLink className="h-3.5 w-3.5 shrink-0" /> {p.name} – Rechnungsseite öffnen
                </a>
              ))}
            </div>
          </div>
        )}

        {focused ? (
          <button type="button" onClick={() => setShowAll(true)} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-indigo-700 self-start" data-testid="receipt-sources-show-all">
            <ChevronDown className="h-3.5 w-3.5" /> Anderes Portal / alle Portale anzeigen
          </button>
        ) : (
          <>
            <p className="text-xs text-slate-500 -mt-1">
              Direkt-Links zu den Rechnungsseiten der Anbieter. Tipp: Die meisten Anbieter können
              Rechnungen auch automatisch per E-Mail senden — an deine Beleg-Inbox weiterleiten,
              dann werden sie automatisch erfasst.
            </p>
            <div className="rounded-md border border-dashed border-slate-300 p-2.5" data-testid="receipt-sources-add-box">
              <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Eigene Webseite hinzufügen</p>
              <form onSubmit={add} className="flex gap-2 items-center">
                <Input placeholder="Name (z.B. Hosting XY)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="receipt-source-name-input" />
                <Input placeholder="URL zur Rechnungsseite" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="receipt-source-url-input" />
                <Button type="submit" size="sm" disabled={busy} data-testid="receipt-source-add-btn"><Plus className="h-4 w-4" /></Button>
              </form>
            </div>
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat}>
                <p className="text-[11px] uppercase tracking-wide text-slate-400 mt-2 mb-1">{cat}</p>
                <div className="space-y-1">
                  {items.map((s, i) => (
                    <div key={s.id || `${cat}-${i}`} className="flex items-center justify-between gap-2 text-sm border border-slate-100 rounded-md px-2.5 py-1.5 hover:bg-slate-50" data-testid={`receipt-source-${s.id || i}`}>
                      <a href={s.url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-slate-700 hover:text-indigo-700 truncate">
                        <ExternalLink className="h-3.5 w-3.5 shrink-0" /> {s.name}
                      </a>
                      {s.custom && (
                        <button onClick={() => remove(s.id)} className="text-slate-300 hover:text-rose-600" data-testid={`receipt-source-delete-${s.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
        <ReceiptRobotSection companyId={companyId} initialPreset={initialPreset} presetKeys={focused ? focusPortals.map((p) => p.key) : null} />
      </DialogContent>
    </Dialog>
  );
};
